import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CodeIcon,
  UserGroupIcon,
  LightningBoltIcon,
  DeviceMobileIcon,
  MoonIcon,
  DocumentDuplicateIcon,
  ShieldCheckIcon,
  ChatAlt2Icon,
  CloudUploadIcon,
} from "@heroicons/react/outline";
import LandingHeader from "../components/LandingHeader";
import LandingFooter from "../components/LandingFooter";
import AboutPage from "./AboutPage";
import ContactPage from "./ContactPage";

function FeatureCard({ image, icon, title, description, reverse }) {
  return (
    <div
      className={`flex flex-col ${
        reverse ? "md:flex-row-reverse" : "md:flex-row"
      } items-center bg-white rounded-2xl shadow-lg p-6 md:p-10 gap-8 mb-12 transition-transform hover:scale-[1.02]`}
    >
      {image ? (
        <img
          className="w-full max-w-xs md:max-w-sm rounded-xl shadow-md object-cover"
          src={image}
          alt={title}
        />
      ) : (
        <div className="flex items-center justify-center w-32 h-32 bg-palette_color1/10 rounded-xl">
          {icon &&
            React.createElement(icon, { className: "h-16 w-16 text-palette_color1" })}
        </div>
      )}
      <div className="flex-1 text-center md:text-left">
        <h3 className="text-2xl font-bold text-palette_color1 mb-2 font-robo">{title}</h3>
        <p className="text-gray-600 font-robo text-base">{description}</p>
      </div>
    </div>
  );
}

function HowItWorksStep({ icon, title, description }) {
  return (
    <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-md">
      {icon &&
        React.createElement(icon, { className: "h-12 w-12 text-palette_color1 mb-4" })}
      <h4 className="text-lg font-bold text-palette_color1 mb-2 font-robo">{title}</h4>
      <p className="text-gray-600 font-robo text-base">{description}</p>
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-200">
      <LandingHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section
          id="home"
          className="py-16 md:py-28 flex flex-col items-center text-center"
        >
          <h1 className="text-5xl md:text-7xl font-black text-palette_color1 font-robo2 mb-6 leading-tight drop-shadow-lg">
            Share it
            <br />
            Run it
          </h1>
          <button
            className="bg-palette_color1 text-white font-bold py-3 px-8 rounded-full shadow-lg hover:bg-palette_color1/90 transition text-lg"
            onClick={() => navigate("/sign-up")}
          >
            Get Started
          </button>
          <div className="mt-12 w-full flex justify-center">
            <img
              className="w-full max-w-3xl rounded-3xl shadow-xl border-4 border-white"
              src={require("../assets/mainscreen.png")}
              alt="Main Screen"
            />
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-palette_color1 mb-12 font-robo2">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <HowItWorksStep
              icon={DocumentDuplicateIcon}
              title="Share Code Instantly"
              description="Paste or upload your code and share it with anyone in seconds."
            />
            <HowItWorksStep
              icon={LightningBoltIcon}
              title="Run & Collaborate"
              description="Run code live, collaborate in real-time, and get instant feedback."
            />
            <HowItWorksStep
              icon={UserGroupIcon}
              title="Connect & Grow"
              description="Join groups, follow developers, and grow your coding network."
            />
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-palette_color1 mb-12 font-robo2">
            Features
          </h2>
          <FeatureCard
            image={require("../assets/codesnip.png")}
            title="Code Snippets"
            description="Share your code snippets with the world in a beautiful, readable format."
          />
          <FeatureCard
            image={require("../assets/forum.png")}
            title="Community Forum"
            description="Communicate, ask questions, and get help from developers everywhere."
            reverse
          />
          <FeatureCard
            image={require("../assets/groupscreen.png")}
            title="Groups"
            description="Create or join groups to collaborate and share experiences."
          />
          <FeatureCard
            image={require("../assets/sendcode.png")}
            title="Send Code Snippets"
            description="Send code snippets instantly and easily to anyone."
            reverse
          />
          <FeatureCard
            image={require("../assets/editcode.png")}
            title="Edit Code Snippets"
            description="Edit your code snippets directly within the chat."
          />
          <FeatureCard
            image={require("../assets/runcode.png")}
            title="Run Code Snippets"
            description="Run your code snippets as soon as you receive them."
            reverse
          />
          <FeatureCard
            image={require("../assets/space.png")}
            title="Code Space"
            description="Save your files securely in the code space cloud."
          />
          {/* New Real Features with Icons */}
          <FeatureCard
            icon={CodeIcon}
            title="Syntax Highlighting"
            description="Supports 20+ programming languages with beautiful syntax highlighting."
            reverse
          />
          <FeatureCard
            icon={LightningBoltIcon}
            title="Instant Code Execution"
            description="Run code in a secure sandbox and see results immediately."
          />
          <FeatureCard
            icon={ShieldCheckIcon}
            title="Secure File Sharing"
            description="Share files and code securely with end-to-end encryption."
            reverse
          />
          <FeatureCard
            icon={ChatAlt2Icon}
            title="Group & Private Chat"
            description="Chat in groups or privately, with real-time notifications."
          />
          <FeatureCard
            icon={CloudUploadIcon}
            title="Version History"
            description="Track changes and roll back to previous versions of your code."
            reverse
          />
          <FeatureCard
            icon={DeviceMobileIcon}
            title="Mobile Friendly"
            description="Enjoy a seamless experience on any device, including mobile and tablet."
          />
          <FeatureCard
            icon={MoonIcon}
            title="Dark Mode"
            description="Switch between light and dark themes for comfortable coding."
            reverse
          />
        </section>

        {/* About Section */}
        <section id="about" className="py-16">
          <AboutPage />
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-16 bg-[#393C41] rounded-t-3xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ContactPage />
            <div className="mt-8">
              <LandingFooter />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default LandingPage;
